/**
 * InactivityWarningModal Component - Modal warning for session timeout
 * Shows countdown timer and allows user to stay logged in or log out
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
} from "@mui/material";
import { Warning } from "@mui/icons-material";

const COUNTDOWN_DURATION = 30; // seconds

/**
 * InactivityWarningModal Component
 * @param {boolean} open - Whether the modal is open
 * @param {Function} onStayLoggedIn - Callback when user chooses to stay logged in
 * @param {Function} onLogout - Callback when user chooses to log out or countdown ends
 */
const InactivityWarningModal = ({ open, onStayLoggedIn, onLogout }) => {
  const [countdown, setCountdown] = useState(COUNTDOWN_DURATION);

  useEffect(() => {
    if (!open) {
      setCountdown(COUNTDOWN_DURATION);
      return;
    }

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          if (onLogout) {
            onLogout();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open, onLogout]);

  const progress = (countdown / COUNTDOWN_DURATION) * 100;

  return (
    <Dialog
      open={open}
      onClose={onStayLoggedIn}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Warning color="warning" />
          <Typography variant="h6">Session Timeout Warning</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" paragraph>
          You have been inactive for 5 minutes. Your session will expire in{" "}
          <strong>{countdown} seconds</strong>.
        </Typography>
        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            color={countdown <= 10 ? "error" : "warning"}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Click &quot;Stay Logged In&quot; to continue your session, or
          &quot;Log Out&quot; to end it now.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onLogout} color="error" variant="outlined">
          Log Out
        </Button>
        <Button
          onClick={onStayLoggedIn}
          color="primary"
          variant="contained"
          autoFocus
        >
          Stay Logged In
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InactivityWarningModal;
